import React, { useEffect, useRef, useState } from 'react';
import { GalleryImage } from '@/lib/storage';
import { X, Star } from 'lucide-react';

interface ImageModalProps {
  image: GalleryImage | null;
  images: GalleryImage[];
  isAdmin: boolean;
  onClose: () => void;
  onEdit: (image: GalleryImage) => void;
  onDelete: (id: string) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
}

export const ImageModal: React.FC<ImageModalProps> = ({
  image,
  images,
  isAdmin,
  onClose,
  onNavigate,
}) => {
  const [rotation, setRotation] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (image) {
      setRotation(image.rotation || 0);
    }
  }, [image]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!image) return;
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onNavigate('prev');
          break;
        case 'ArrowRight':
          onNavigate('next');
          break;
        case ' ':
          e.preventDefault();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [image, onClose, onNavigate]);

  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.style.transform = `rotate(${rotation}deg)`;
    }
  }, [rotation]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!image) return null;

  const currentIndex = images.findIndex(i => i.id === image.id);

  const brandColors: Record<string, string> = {
    used: 'gold',
    honda: 'red',
    hero: 'blue',
    sym: 'green',
    kayo: 'orange',
    bajaj: 'purple',
    lifan: 'pink',
    other: 'gray',
    stripping: 'amber',
  };
  const color = brandColors[image.brandId] || 'gold';

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm animate-fade-in"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={`${image.alt} - Full view`}
    >
      <div className="relative w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-4 text-neutral-300 text-sm">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${color === 'gold' ? 'bg-gold/20 text-gold' : `bg-${color}-500/20 text-${color}-400`}`}>
              {image.brandId.toUpperCase()}
            </span>
            <span className="font-mono">#{currentIndex + 1} / {images.length}</span>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && image.isFeatured && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-gold/20 text-gold text-[10px] font-bold uppercase tracking-wider">
                <Star className="h-3 w-3 fill-current" /> Featured
              </span>
            )}
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-xl bg-dark-card border border-dark-border flex items-center justify-center hover:bg-dark-hover hover:border-neutral-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-dark-bg rounded-2xl">
          <div className="relative w-full h-full flex items-center justify-center touch-pan-x touch-pan-y">
            <img
              ref={imgRef}
              src={image.url}
              alt={image.alt}
              className="max-w-full max-h-[84vh] object-contain transition-transform duration-100"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-dark-card border border-dark-border rounded-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-semibold text-white truncate max-w-[300px]">{image.alt || 'Untitled'}</h3>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-black text-xl text-gold">JB Motorcycles</span>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-center gap-1.5" role="tablist" aria-label="Image thumbnails">
          {images.slice(Math.max(0, currentIndex - 3), currentIndex + 4).map((img, i) => (
            <button
              key={img.id}
              onClick={() => onNavigate(i < 3 ? 'prev' : 'next')}
              className={`h-14 w-20 rounded-lg border-2 transition-all flex items-center justify-center overflow-hidden $
                img.id === image.id
                  ? 'border-gold ring-2 ring-gold/20'
                  : 'border-dark-border hover:border-gold/30'
              }`}
              role="tab"
              aria-selected={img.id === image.id}
              aria-label={`View image ${images.findIndex(x => x.id === img.id) + 1}`}
            >
              <img src={img.url} alt={img.alt} className="w-full h-full object-contain rounded" loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};